import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { FieldIssue } from 'api/crm/intakeFormAPI';
import { colorsTheme } from 'resources/theme/styled/colors';
import { useEntityIntakeFormContext } from 'modules/IntakeForm/EntityIntakeForm/EntityIntakeFormContext';
import { ResolveFieldCommentValidationSchema } from 'validations/intake-form/intake-form-steps';

// components
import SubmitButton from 'components/Buttons/SubmitButton';
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import { ReactComponent as ArrowRight } from 'resources/icons/remix-icons/arrow-right-s-line.svg';
import {
  Col,
  Row,
  Form,
  Text,
  Divider,
  FormField,
  FormInputTextArea,
  Link,
  IconSVG,
  Tooltip,
} from '@ui';

interface FormValuesModel {
  fieldResolvedAlready: boolean;
  comment: string;
}

interface IProps {
  fieldIssue: FieldIssue;
  disabled: boolean;
}

const LeaveCommentForm = ({ disabled, fieldIssue }: IProps) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const {
    haveNotResolvedComments,
    resolveFieldComment,
    navigateToNextComment,
    setOpenedFieldIssuePopoverId,
  } = useEntityIntakeFormContext();

  const initialValues = useMemo<FormValuesModel>(() => {
    const comment = fieldIssue.resolvedWithComment || '';

    return {
      fieldResolvedAlready: !!comment,
      comment,
    };
  }, [fieldIssue]);

  const handleSubmit = async (values: FormValuesModel) => {
    await resolveFieldComment(fieldIssue.fieldId, values.comment);
    setOpenedFieldIssuePopoverId(null);
  };

  return (
    <Form<FormValuesModel>
      disabled={disabled}
      enableReinitialize
      confirmExitWithoutSaving={false}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={ResolveFieldCommentValidationSchema}
      renderForm={({ values }) => (
        <>
          {disabled ? (
            values.comment ? (
              <>
                <StyledDivider />
                <Text>
                  {t('intake_form.review_field_issues.response_comment')}
                </Text>
                <Text>{values.comment}</Text>
              </>
            ) : null
          ) : (
            <>
              <StyledDivider />
              <FormField
                label={t('intake_form.review_field_issues.response_comment')}
                name="comment"
                component={FormInputTextArea}
                additionalProps={{
                  rows: 3,
                }}
              />
            </>
          )}
          <HideIfDisabledForm>
            <Row gutter={[16, 16]} justify="space-between" align="middle">
              <Col>
                <StyledSubmitButton size="middle">
                  {values.fieldResolvedAlready
                    ? t('save', { ns: 'common' })
                    : t('intake_form.review_field_issues.mark_as_resolved')}
                </StyledSubmitButton>
              </Col>
              <Col>
                <Tooltip
                  title={
                    !haveNotResolvedComments
                      ? t(
                          'intake_form.review_field_issues.all_comments_reviewed_popover',
                        )
                      : undefined
                  }
                >
                  <LinkWrapper disabled={!haveNotResolvedComments}>
                    <Link
                      onClick={
                        haveNotResolvedComments
                          ? navigateToNextComment
                          : undefined
                      }
                    >
                      <DivAlignCenter>
                        {t('intake_form.review_field_issues.next_comment')}
                        <StyledIconSVG
                          component={ArrowRight}
                          color={colorsTheme.colorWhite}
                        />
                      </DivAlignCenter>
                    </Link>
                  </LinkWrapper>
                </Tooltip>
              </Col>
            </Row>
          </HideIfDisabledForm>
        </>
      )}
    />
  );
};

const StyledSubmitButton = styled(SubmitButton)`
  min-width: none;
`;

const StyledDivider = styled(Divider)`
  margin: ${({ theme }) => `${theme.marginXXs} 0px`};
`;

const LinkWrapper = styled.div<{ disabled: boolean }>`
  ${({ disabled }) =>
    disabled &&
    `
    cursor: not-allowed;
    pointer-events: all !important;
    opacity: 0.5;
    * {
      cursor: not-allowed !important;
    }
  `}
`;
const StyledIconSVG = styled(IconSVG)`
  cursor: pointer;
`;

export default LeaveCommentForm;
